import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Button.js";
import { getReq } from "../../configs/ApiUrl.js";
import { LoadingSpinner } from "./util/LoadingSpinner.js";
import defaultImg from "../../images/defaultImg.webp";
import DataTable from "../DataTable.js";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";

function UserSell() {
  const { t } = useTranslation([
    "userProfile",
    "tables",
    "altText",
    "subadmin",
  ]);

  const [loading, setLoading] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const myProductsResp = await getReq("/product/user/products");
        if (myProductsResp?.success) {
          setMyProducts(myProductsResp.myProducts);
        } else {
          console.error("Failed to fetch products.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const STATUS_STEPS = ["Added", "Approved", "Bought", "Delivered"];
  const STATUS_MAP = {
    pending: 0,
    approved: 1,
    sold: 2,
    deliveryApproved: 3,
  };
  const STATUS_MESSAGES = {
    pending: (title) => `${title} is awaiting admin approval`,
    approved: (title) => `${title} is live on our marketplace`,
    sold: (title) => `${title} is due to be delivered to the buyer`,
    deliveryApproved: (title) => `${title} has been delivered`,
  };

  const getStatusMessage = (status, title) => {
    const fn = STATUS_MESSAGES[status];
    return fn ? fn(title) : "";
  };

  const renderStepProgress = (product) => {
    const currentStepIndex = STATUS_MAP[product.status] || 0;
    return (
      <div className='flex items-center'>
        {STATUS_STEPS.map((label, idx) => {
          const circleCompleted = currentStepIndex >= idx;
          const lineCompleted = currentStepIndex > idx;

          const circle = circleCompleted
            ? "w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
            : "w-6 h-6 border-2 border-gray-300 rounded-full";
          const line = lineCompleted
            ? "h-1 flex-1 bg-green-500"
            : "h-1 flex-1 bg-gray-300";

          return (
            <React.Fragment key={idx}>
              {idx !== 0 && <div className={line}></div>}
              <div className='flex flex-col items-center mx-1'>
                <div className={circle}>
                  {circleCompleted && (
                    <svg
                      className='w-4 h-4 text-white'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                  )}
                </div>
                <div className='text-xs mt-1'>{label}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const columns = [
    {
      header: t("tables:product"),
      accessor: (row) => <div className='font-medium'>{row.title}</div>,
      align: "left",
      mobile: true,
      mobileChip: false,
    },
    {
      header: t("tables:status"),
      accessor: (row) => (
        <div className='text-gray-500 text-sm'>
          {getStatusMessage(row.status, row.title)}
        </div>
      ),
      align: "left",
      mobile: true,
      mobileChip: true,
    },
    {
      header: t("tables:progress"),
      accessor: (row) => renderStepProgress(row),
      align: "left",
      mobile: false,
      mobileChip: false,
    },
  ];

  const getImage = (row) => row.mainImage || row.imageNames?.[0] || defaultImg;

  const getRowKey = (row) => row._id;

  const renderRowActions = () => null;

  return (
    <div className='flex flex-col items-center'>
      <main className='w-full flex flex-col justify-center drop-shadow'>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <DataTable
            data={myProducts}
            columns={columns}
            getImage={getImage}
            getRowKey={getRowKey}
            renderRowActions={renderRowActions}
            emptyMessage={t("userProfile:noProducts")}
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={(e, newPage) => setPage(newPage)}
            handleRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
        <div className='flex justify-center my-12'>
          <Button
            variant='main'
            size='md'
            startIcon={<AddIcon />}
            onClick={() => navigate("/selling-add-furniture")}>
            {t("subadmin:newProductButton")}
            </Button>
        </div>
      </main>
    </div>
  );
}

export default UserSell;
